<template>
  <div class="seedling-raisingfactory">
    <!-- 顶部设备状态栏 -->
    <div class="status-bar">
      <span class="status-label">设备状态：</span>
      <span class="status-text">{{ deviceStatus }}</span>
    </div>

    <!-- 流程图画布 -->
    <div class="flow-wrapper">
      <div class="flow-canvas">
        <!-- 区域标签（横排） -->
        <div v-for="(label, idx) in areaLabels" :key="'area-' + idx" class="area-label" :style="{ top: label.top + 'px', left: label.left + 'px' }">
          {{ label.text }}
        </div>

        <!-- 连接线（输送带） -->
        <div
          v-for="(line, idx) in lines"
          :key="'line-' + idx"
          class="flow-line"
          :class="[line.type, { thick: line.thick }]"
          :style="lineStyle(line)"
        ></div>

        <!-- 箭头（向上指向播种区） -->
        <div v-for="(arrow, idx) in arrows" :key="'arrow-' + idx" class="flow-arrow" :style="arrowStyle(arrow)"></div>

        <!-- 流程节点 -->
        <div
          v-for="node in processNodes"
          :key="node.id"
          class="process-node"
          :class="{ active: node.active, wide: node.wide }"
          :style="nodeStyle(node)"
        >
          {{ node.label }}
        </div>

        <!-- 状态指示灯 -->
        <div
          v-for="(ind, idx) in indicators"
          :key="'ind-' + idx"
          class="indicator-item"
          :class="ind.dir || 'left'"
          :style="{ top: ind.top + 'px', left: ind.left + 'px' }"
        >
          <span class="status-dot" :class="{ on: ind.on }"></span>
          <span v-if="ind.label" class="indicator-label">{{ ind.label }}</span>
        </div>

        <!-- 副标签（工作位松开 / 工作位顶升 / 工作位下降） -->
        <div v-for="(sub, idx) in subLabels" :key="'sub-' + idx" class="sub-label" :style="{ top: sub.top + 'px', left: sub.left + 'px' }">
          {{ sub.text }}
        </div>

        <!-- 机械臂 -->
        <div class="robot-arm" :style="{ top: robotArm.top + 'px', left: robotArm.left + 'px' }">
          <svg width="100" height="90" viewBox="0 0 100 90" xmlns="http://www.w3.org/2000/svg">
            <!-- 底座 -->
            <rect x="28" y="72" width="44" height="14" rx="2" fill="#d0d0d0" stroke="#999" />
            <rect x="36" y="66" width="28" height="8" fill="#e8e8e8" stroke="#bbb" />
            <!-- 立柱 -->
            <rect x="44" y="38" width="12" height="30" fill="#f5f5f5" stroke="#ccc" />
            <!-- 肩关节 -->
            <circle cx="50" cy="38" r="9" fill="#ff4d4d" stroke="#c0392b" stroke-width="1.5" />
            <!-- 大臂 -->
            <rect x="48" y="8" width="10" height="34" rx="2" fill="#fff" stroke="#bbb" transform="rotate(-35 50 38)" />
            <!-- 肘关节 -->
            <!-- <circle cx="68" cy="18" r="5" fill="#ff4d4d" stroke="#c0392b" /> -->
            <!-- 小臂 -->
            <!-- <rect x="66" y="4" width="8" height="22" rx="1" fill="#eee" stroke="#bbb"
              transform="rotate(25 70 18)" /> -->
            <!-- 夹爪 -->
            <!-- <path d="M78 6 L88 2 L88 10 Z" fill="#ff4d4d" />
            <path d="M78 14 L88 18 L88 10 Z" fill="#ff4d4d" /> -->
          </svg>
        </div>
      </div>
    </div>

    <!-- 底部统计与控制 -->
    <div class="footer-panel">
      <div class="stats">
        <div class="stat-item">
          <span class="stat-label">抓取次数</span>
          <span class="stat-value">{{ grabCount }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">秧盘个数</span>
          <span class="stat-value">{{ trayCount }}</span>
        </div>
      </div>
      <div class="controls">
        <button class="btn-clear" @click="handleClear">清零</button>
        <button class="btn-start" @click="handleStart">启动</button>
        <button class="btn-stop" @click="handleStop">停止</button>
        <button class="btn-reset" @click="handleReset">复位</button>
      </div>
    </div>

    <MyNav ref="MyNav" />
  </div>
</template>

<script>
import { mapMutations } from 'vuex'

export default {
  components: {
    MyNav: () => import('@/components/MyNav.vue')
  },
  data() {
    return {
      deviceStatus: '待机中',
      grabCount: 0,
      trayCount: 0,
      // 机械臂：叠盘输送下方偏右
      robotArm: { top: 180, left: 780 },
      // 左侧区域标签（横排）
      areaLabels: [
        { text: '托盘库区', top: 300, left: 0 },
        { text: '托盘移栽区', top: 380, left: 0 },
        { text: '码垛区', top: 480, left: 0 }
      ],
      // 副标签
      subLabels: [
        { text: '工作位松开', top: 352, left: 348 },
        { text: '工作位顶升', top: 535, left: 520 },
        { text: '工作位下降', top: 535, left: 695 }
      ],
      processNodes: [
        // —— 左上入口 ——
        { id: 'fenpan', label: '分盘供电', top: 66, left: 100, width: 96, height: 34 },
        { id: 'yangpan-left', label: '秧盘输送', top: 100, left: 100, width: 96, height: 34 },
        // —— 上方蓝色长条 ——
        { id: 'yangpan-top', label: '秧盘输送', top: 28, left: 280, width: 560, height: 30, active: true, wide: true },
        // —— 主流程横线 ——
        { id: 'putu', label: '铺土输送', top: 100, left: 260, width: 96, height: 34 },
        { id: 'bozhong', label: '播种', top: 100, left: 430, width: 96, height: 34 },
        { id: 'futu', label: '覆土输送', top: 100, left: 600, width: 96, height: 34 },
        { id: 'diepan', label: '叠盘输送', top: 100, left: 770, width: 96, height: 34 },
        // —— 播种下方：洒水 / 种子机（同高） ——
        { id: 'sanshui', label: '洒水', top: 178, left: 352, width: 80, height: 34 },
        { id: 'zhongziji', label: '种子机', top: 178, left: 432, width: 90, height: 34, active: true },
        // —— 中部托盘区 ——
        { id: 'tuopan-sheng', label: '托盘升', top: 280, left: 205, width: 80, height: 34 },
        { id: 'tuopan-jiang', label: '托盘降', top: 280, left: 340, width: 80, height: 34 },
        { id: 'jiatuo', label: '夹托气缸', top: 315, left: 340, width: 80, height: 34 },
        { id: 'lian-tiao', label: '链条输送', top: 280, left: 500, width: 96, height: 34 },
        // —— 底部码垛分支 ——
        { id: 'dingsheng-shu', label: '顶升输送', top: 460, left: 500, width: 96, height: 34 },
        { id: 'dingsheng-qi', label: '顶升气缸', top: 495, left: 500, width: 96, height: 34 },
        { id: 'maduo-shu', label: '码垛输送', top: 460, left: 675, width: 96, height: 34 },
        { id: 'dingwei', label: '定位气缸', top: 495, left: 675, width: 96, height: 34 },
        // —— 右端叉取 ——
        { id: 'chaqu', label: '叉取输送', top: 460, left: 840, width: 96, height: 34 }
      ],
      indicators: [
        // 托盘升左侧：上限灰 / 中限灰 / 下限绿；托盘升左侧另有绿点
        { label: '上限', top: 230, left: 290, on: false, dir: 'left' },
        { label: '中限', top: 230, left: 360, on: false, dir: 'left' },
        { label: '下限', top: 230, left: 430, on: true, dir: 'left' },
        { label: '', top: 288, left: 185, on: true, dir: 'none' },
        // 托盘降左侧绿点；右侧托盘库检测灰
        { label: '', top: 288, left: 320, on: true, dir: 'none' },
        { label: '托盘库检测', top: 230, left: 500, on: false, dir: 'right' },
        // 夹托气缸：松开灰 / 夹紧绿
        { label: '松开', top: 325, left: 290, on: false, dir: 'right' },
        { label: '夹紧', top: 325, left: 425, on: true, dir: 'right' },
        // 顶升气缸旁：上限灰 / 下限绿 / 移栽检测灰
        { label: '上限', top: 495, left: 440, on: false, dir: 'left' },
        { label: '下限', top: 519, left: 440, on: true, dir: 'left' },
        { label: '移栽检测', top: 430, left: 500, on: false, dir: 'left' },
        // 码垛/定位：码垛检测灰；气缸旁绿点
        { label: '码垛检测', top: 430, left: 750, on: false, dir: 'right' },
        { label: '', top: 505, left: 780, on: true, dir: 'none' },
        // 叠盘上方状态点（灰）
        { label: '', top: 78, left: 818, on: false, dir: 'none' },
        // 叉取：可叉取灰
        { label: '可叉取', top: 300, left: 880, on: false, dir: 'right' }
      ],
      lines: [
        // 分盘供电 ↔ 秧盘输送（左）
        // { type: 'v', top: 70, left: 146, height: 8, thick: true },
        // 左入口 → 铺土输送
        { type: 'h', top: 114, left: 196, width: 84, thick: true },
        // { type: 'v', top: 53, left: 280, height: 47, thick: true },
        // 蓝色长条下方到主线
        // { type: 'v', top: 58, left: 478, height: 42, thick: true },
        // { type: 'v', top: 58, left: 648, height: 42, thick: true },
        // 主流程横连（输送带感）
        { type: 'h', top: 114, left: 356, width: 74, thick: true },
        { type: 'h', top: 114, left: 526, width: 74, thick: true },
        { type: 'h', top: 114, left: 696, width: 74, thick: true },
        // 播种 → 洒水 / 种子机（向上箭头区域竖线）
        { type: 'v', top: 123, left: 393, height: 55, thick: false },
        { type: 'v', top: 134, left: 475, height: 44, thick: false },
        // 叠盘 → 机械臂 → 底部
        { type: 'v', top: 134, left: 818, height: 66, thick: true },
        { type: 'v', top: 330, left: 885, height: 130, thick: true },
        // { type: 'h', top: 290, left: 818, width: 142, thick: true },
        // 托盘升/降竖连
        // { type: 'v', top: 314, left: 258, height: 16, thick: true },
        // { type: 'v', top: 364, left: 258, height: 16, thick: true },
        // 托盘降 → 链条输送
        { type: 'h', top: 294, left: 400, width: 120, thick: true },
        // 链条 → 底部码垛区
        // { type: 'v', top: 364, left: 468, height: 96, thick: true },
        // { type: 'h', top: 460, left: 468, width: 32, thick: true },
        // 顶升 ↔ 码垛
        { type: 'h', top: 474, left: 596, width: 80, thick: true },
        // { type: 'v', top: 494, left: 548, height: 16, thick: false },
        // { type: 'v', top: 494, left: 768, height: 16, thick: false },
        // 码垛 → 叉取
        { type: 'h', top: 474, left: 770, width: 70, thick: true }
        // 铺土 ← 左上竖干线延伸到托盘区
        // { type: 'v', top: 134, left: 308, height: 146, thick: true },
        // { type: 'h', top: 280, left: 258, width: 50, thick: true }
      ],
      // 向上箭头（指向播种区）
      arrows: [
        { top: 148, left: 389, rotate: 0 },
        { top: 148, left: 470, rotate: 0 },
        // 主流程方向箭头
        // { top: 106, left: 390, rotate: 90 },
        // { top: 106, left: 560, rotate: 90 },
        // { top: 106, left: 730, rotate: 90 },
        // { top: 466, left: 656, rotate: 90 },
        // { top: 466, left: 900, rotate: 90 }
      ]
    }
  },
  mounted() {
    this.SET_NAV({ nav: 3 })
    this.$nextTick(() => {
      if (this.$refs.MyNav && this.$refs.MyNav.$refs.refLeft) {
        const left = this.$refs.MyNav.$refs.refLeft[3].offsetLeft + 'px'
        sessionStorage.setItem('navLeft', left)
      }
    })
  },
  methods: {
    ...mapMutations('user', ['SET_NAV']),
    nodeStyle(node) {
      return {
        top: node.top + 'px',
        left: node.left + 'px',
        width: (node.width || 90) + 'px',
        height: (node.height || 34) + 'px'
      }
    },
    lineStyle(line) {
      const style = {
        top: line.top + 'px',
        left: line.left + 'px'
      }
      if (line.type === 'h') {
        style.width = line.width + 'px'
      } else {
        style.height = line.height + 'px'
      }
      return style
    },
    arrowStyle(arrow) {
      return {
        top: arrow.top + 'px',
        left: arrow.left + 'px',
        transform: `rotate(${arrow.rotate}deg)`
      }
    },
    handleClear() {
      this.grabCount = 0
      this.trayCount = 0
    },
    handleStart() {
      this.$message.success('启动')
      this.deviceStatus = '运行中'
    },
    handleStop() {
      this.$message.warning('停止')
      this.deviceStatus = '待机中'
    },
    handleReset() {
      this.$message.info('复位')
    }
  }
}
</script>

<style lang="less" scoped>
.seedling-raisingfactory {
  width: 100%;
  min-height: 100vh;
  padding: 72px 48px 120px;
  background-color: #33384e;
  color: #fff;
  overflow-x: hidden;
  box-sizing: border-box;
}

/* 顶部状态栏 */
.status-bar {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 16px;

  .status-label {
    color: #fff;
  }

  .status-text {
    color: #fff;
    font-weight: bold;
    font-size: 18px;
  }
}

/* 流程图容器 */
.flow-wrapper {
  width: 100%;
  overflow-x: auto;
  margin-bottom: 20px;
}

.flow-canvas {
  position: relative;
  width: 1280px;
  height: 600px;
  margin: 0 auto;
}

/* 区域标签 — 横排 */
.area-label {
  position: absolute;
  font-size: 13px;
  color: #bbb;
  white-space: nowrap;
  letter-spacing: 1px;
}

.sub-label {
  position: absolute;
  font-size: 12px;
  color: #999;
  white-space: nowrap;
}

/* 连接线 */
.flow-line {
  position: absolute;
  background-color: #7a7a7a;

  &.h {
    height: 3px;

    &.thick {
      height: 8px;
      background-color: #6e6e6e;
      border-radius: 1px;
    }
  }

  &.v {
    width: 3px;

    &.thick {
      width: 8px;
      background-color: #6e6e6e;
      border-radius: 1px;
    }
  }
}

/* CSS 三角箭头 */
.flow-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 10px solid #ddd;
  transform-origin: center center;
}

/* 流程节点 */
.process-node {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #111;
  background-color: #8c8c8c;
  color: #111;
  font-size: 13px;
  text-align: center;
  box-sizing: border-box;
  z-index: 2;

  &.active {
    background-color: #2074d4;
    color: #fff;
  }

  &.wide {
    font-size: 14px;
  }
}

/* 状态指示灯 */
.indicator-item {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  z-index: 3;

  &.left {
    flex-direction: row;
  }

  &.right {
    flex-direction: row;
  }

  &.none {
    gap: 0;
  }
}

.status-dot {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: #b0b0b0;
  flex-shrink: 0;
  border: 1px solid #888;

  &.on {
    background-color: #4fcb8d;
    border-color: #3aa875;
  }
}

.indicator-label {
  font-size: 12px;
  color: #ccc;
}

/* 机械臂 */
.robot-arm {
  position: absolute;
  pointer-events: none;
  z-index: 2;
}

/* 底部面板 */
.footer-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 40px 0;
  max-width: 1280px;
  margin: 0 auto;
}

.stats {
  display: flex;
  gap: 72px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stat-label {
  font-size: 14px;
  color: #bbb;
}

.stat-value {
  font-size: 40px;
  font-weight: bold;
  color: #4fcb8d;
  line-height: 1;
}

.controls {
  display: flex;
  align-items: center;
  gap: 28px;
}

.btn-clear {
  width: 88px;
  height: 42px;
  background-color: #8c8c8c;
  border: 1px solid #666;
  color: #fff;
  font-size: 15px;
  cursor: pointer;
  border-radius: 2px;

  &:hover {
    background-color: #999;
  }
}

.btn-start,
.btn-stop,
.btn-reset {
  width: 72px;
  height: 72px;
  border: none;
  border-radius: 50%;
  color: #111;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);

  &:hover {
    opacity: 0.88;
  }
}

.btn-start {
  background: linear-gradient(180deg, #6ed9a4 0%, #4fcb8d 100%);
}

.btn-stop {
  background: linear-gradient(180deg, #ff9a9a 0%, #ff7676 100%);
}

.btn-reset {
  background: linear-gradient(180deg, #4a96e8 0%, #2074d4 100%);
  color: #fff;
}
</style>
